// src/pages/JoinPage.js
//
// Public subscription marketing page at /join. Shareable URL Danielle posts to
// Instagram / email / her network to drive the $15/mo subscription. Not linked
// from any nav — direct URL only. Mirrors the /terms public-route pattern.
//
// Audience: both new prospects (signed-out) and existing app users (signed-in,
// not yet subscribed). Renders identically for both; the primary CTA's behavior
// branches by auth state.
//
// CTA matrix:
//   • not loaded     → "Subscribe" disabled
//   • signed-out     → SignUpButton modal; auto-fires checkout once signup
//                       completes (justSignedUpRef one-shot)
//   • subscribed     → "You're already in" → /workouts
//   • signed-in/not  → POST /api/class-checkout (same call as every other
//                       in-app Subscribe button)
//
// All copy + photos editable via siteConfig (AdminDashboard → Join Page tab).
// Hardcoded English defaults below mean the page renders legibly even when
// Firestore is unreachable. Photos are Bunny URLs picked via the existing
// admin Browse Uploaded Images flow.
//
// Testimonials are sourced from siteConfig.testimonials (the same array driving
// homepage testimonials — single source of truth, managed on the Homepage tab).
// Top 5 shown here, "See all" overflow modal pattern matches LandingPage.
//
// See docs/ux/join-marketing-page.md + docs/decisions/adr-join-marketing-page.md.

import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser, useAuth, SignUpButton, SignInButton } from '@clerk/clerk-react';
import { fetchSiteContent, getCachedSiteContent } from '../services/firebase';
import { hasActiveSubscription } from '../services/classAccess';
import FormattedText from '../components/FormattedText';
import { PlanOptions, useAnnualOffer } from '../components/PlanPicker';
import { resolvePlanCopy, rememberPlanChoice, consumePlanChoice } from '../services/subscriptionPlans';

// ── Hardcoded defaults ──────────────────────────────────────────────────────
// Used when siteConfig is unreachable OR when a specific field is empty.
// Mirrors the TermsPage defensive pattern. Copy is invitational per
// `feedback_invitational_copy` — invitation to move, not remediation pitch.
const DEFAULTS = {
    joinHeroEyebrow: 'UNLIMITED · WITH DANIELLE',
    joinHeroHeadline: 'Show up for yourself,\non your schedule.',
    joinHeroSubcopy: 'Unlimited live classes plus a growing library of on-demand workouts — all for the price of one boutique class. Move when you can, where you can.',
    joinPrimaryCta: 'Start Moving',
    joinReassurance: 'Cancel anytime · No commitment',
    // Shown in place of BOTH Subscribe CTAs (hero + bottom) when the admin
    // Settings → "Subscription Option" toggle is OFF. See `CtaButton` for
    // the gate. Subscribers continue to see the member view below — closing
    // enrollment does not revoke existing access.
    joinEnrollmentClosedMessage: 'Enrollment opens May 17th @ 11am CT. See you then!',
    // Member view — shown to signed-in subscribers in place of Subscribe.
    // Editorial composition: eyebrow + italic-serif welcome + optional
    // sub-copy + single filled primary button. Subcopy defaults empty.
    joinMemberEyebrow: 'MEMBER ACCESS',
    joinMemberHeadline: "You're already moving with us.",
    joinMemberSubcopy: '',
    joinMemberCta: 'Take me to my workouts →',
    joinHeroImage: '',
    joinSocialProof: 'Join women already moving with Danielle',
    joinNavBrowseLabel: 'Browse classes →',
    joinNavSignInLabel: 'Sign in',

    joinIncludedHeading: 'Everything you need to move',
    joinIncludedSubheading: 'One subscription. Every class.',
    joinIncluded1Title: 'Live classes every week',
    joinIncluded1Desc: 'Move with Danielle in real time. Pre-recorded options too — whatever fits your week.',
    joinIncluded2Title: 'On-demand library',
    joinIncluded2Desc: 'Every past class becomes a replay you can return to anytime.',
    joinIncluded3Title: 'Short, effective workouts',
    joinIncluded3Desc: 'From 5-minute resets to 60-minute strength sessions. Real-life length.',
    joinIncluded4Title: 'Beginner-friendly',
    joinIncluded4Desc: 'Modifications offered every class. "Ease In" tagged classes for fresh starts.',
    joinIncluded5Title: 'Community built in',
    joinIncluded5Desc: 'See who else is in class, comment together, share the wins.',
    joinIncluded6Title: 'Cancel anytime',
    joinIncluded6Desc: 'Month-to-month. No contracts. No commitments.',

    // Lifestyle photo sections — three tiles each, magazine-triptych layout.
    // Legacy single-image keys (joinLifestyleImage1/2) fall through as
    // tile-1 of each new section so existing uploads don't disappear.
    joinLifestyleImage1: '',
    joinLifestyleImage2: '',
    joinLifestyle1Image1: '',
    joinLifestyle1Image2: '',
    joinLifestyle1Image3: '',
    joinLifestyle2Image1: '',
    joinLifestyle2Image2: '',
    joinLifestyle2Image3: '',

    joinMeetHeading: "Hey, I'm Danielle.",
    joinMeetBody: "I'm a coach, a mom, and a believer that **any** movement counts. The classes I lead here are the ones I wish I'd had when I was just starting out — short enough to actually do on a busy day, real enough to actually feel.\n\nThis is movement that fits your life, not the other way around.",
    joinMeetImage: '',

    joinTestimonialsHeading: 'Real women, real results',
    joinTestimonialsSubheading: 'What members are saying.',

    joinStatsHeading: 'By the numbers',
    joinStat1Num: '5–60',
    joinStat1Label: 'minute options',
    joinStat2Num: 'Weekly',
    joinStat2Label: 'live classes',
    joinStat3Num: 'Anytime',
    joinStat3Label: 'on-demand access',

    joinFaqHeading: 'Questions?',
    joinFaq1Q: 'Can I cancel anytime?',
    joinFaq1A: "Yes. Cancel anytime from your Account page. You'll keep access through the end of your billing period — no questions asked, no friction.",
    joinFaq2Q: 'What if I miss a live class?',
    joinFaq2A: 'Every live class is recorded automatically. The replay shows up in your On Demand library within a few minutes of class ending.',
    joinFaq3Q: "I'm a total beginner. Is this for me?",
    joinFaq3A: 'Yes. Start with the "Ease In" tagged classes — they\'re designed to build confidence without overwhelm. Modifications are offered every class.',
    joinFaq4Q: 'How much is it?',
    joinFaq4A: "It's a flat monthly rate. No commitments, no contracts, no hidden tiers.",
    joinFaq5Q: 'What equipment do I need?',
    joinFaq5A: 'Most classes are bodyweight only. Some use light dumbbells or resistance bands — gear is always called out in the class description so you can plan ahead.',
    // Optional overflow slots 6–10 — empty defaults so they stay hidden until
    // admin fills them in. FaqSection filters out any row where Q or A is
    // empty/whitespace, so unused slots silently disappear from the page.
    joinFaq6Q: '', joinFaq6A: '',
    joinFaq7Q: '', joinFaq7A: '',
    joinFaq8Q: '', joinFaq8A: '',
    joinFaq9Q: '', joinFaq9A: '',
    joinFaq10Q: '', joinFaq10A: '',

    joinBottomHeadline: 'Ready when you are.',
    joinBottomSubcopy: 'One subscription. Every class. Cancel anytime.',
};

// ── Helper: merge fetched siteConfig over defaults ──
// Key-presence wins, NOT non-empty. If a key exists on the fetched doc — even
// with an empty string — admin's value wins (clear-to-hide works). Defaults
// only fill in for keys that were never set at all (legacy docs that predate
// a given field, or a Firestore-unreachable fallback).
//
// Original pattern matched TermsPage's "empty falls through to default", but
// that conflated "admin never touched this" with "admin explicitly cleared it"
// — which broke clear-to-hide on the What's Included tiles (admin clears tile
// title → page kept showing the default). Fixed 2026-05-12 same-day as ship.
function mergeContent(fetched) {
    if (!fetched) return { ...DEFAULTS };
    const merged = { ...DEFAULTS };
    Object.keys(DEFAULTS).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(fetched, key)) {
            merged[key] = fetched[key];
        }
    });
    // Carry through optional fields we don't default — testimonials array,
    // price label, etc. — without overwriting their absence with empty strings.
    merged.testimonials = Array.isArray(fetched.testimonials) ? fetched.testimonials : null;
    // Legacy numbered testimonials fallback (matches LandingPage behavior).
    merged._legacyTestimonialFields = fetched;
    merged.subscriptionPriceLabel = fetched.subscriptionPriceLabel || '$15/mo';
    // Carry through admin's Settings → "Subscription Option" toggle. Boolean,
    // optional — not part of DEFAULTS because the falsy-strict `=== false`
    // check in CtaButton treats `undefined` as "enrollment open" (preserves
    // pre-fetch render + offline-fallback behavior). Mirrors the lock-state
    // semantic in Workouts.js.
    merged.subscriptionEnabled = fetched.subscriptionEnabled;
    // Yearly plan (docs/ux/annual-plan.md). Only a literal `true` turns the
    // plan choice on; absent/false => today's single price chip + monthly checkout.
    merged.enableAnnualPlan = fetched.enableAnnualPlan === true;
    merged.planCopy = resolvePlanCopy(fetched);
    return merged;
}

// ── Resolve testimonials array (supports both new array + legacy numbered format) ──
function resolveTestimonials(content) {
    if (content.testimonials && Array.isArray(content.testimonials)) {
        return content.testimonials.filter((t) => t.text && t.text.trim());
    }
    const legacy = content._legacyTestimonialFields || {};
    const out = [];
    for (let i = 1; i <= 5; i++) {
        const text = legacy[`testimonial${i}Text`];
        if (text && text.trim()) {
            out.push({
                text,
                name: legacy[`testimonial${i}Name`] || '',
                detail: legacy[`testimonial${i}Detail`] || '',
            });
        }
    }
    return out;
}

// ── Subscribe-intent persistence ────────────────────────────────────────
// The auto-fire-to-checkout pattern uses an in-process React ref as its
// primary signal (`justSignedUpRef` inside JoinPage). That works perfectly
// for the sign-UP path: SignUpButton mode="modal" closes in place, no
// remount, ref preserved. But the sign-IN path is different — when an
// existing user signs in via the modal's footer Sign In CTA, Clerk's auth
// state change can trigger a navigation cycle (Clerk's default
// signInFallbackRedirectUrl is `/`, and even when the user lands back on
// /join, React Router can remount the route component under certain
// conditions). A remount resets the ref to false → effect runs after sign-in
// → intent has been silently lost → checkout never fires → user is left
// staring at the same Subscribe button they just clicked.
//
// Fix: shadow the ref with a sessionStorage timestamp. SessionStorage
// survives any in-tab remount but is naturally scoped to the tab session.
// 5-minute TTL self-cleans the rare case where a user clicks Subscribe,
// dismisses the modal without signing in, then later signs in via some
// other path — preventing an unexpected late auto-fire.
//
// Either signal (ref OR fresh sessionStorage timestamp) triggers checkout.
// Both are cleared on fire. The ref path is unchanged and still acts as
// the fast in-process signal for the sign-up flow that was already working.
const SUBSCRIBE_INTENT_KEY = 'mom_join_subscribe_intent_at';
const SUBSCRIBE_INTENT_TTL_MS = 5 * 60 * 1000; // 5 minutes

function markSubscribeIntent() {
    try { sessionStorage.setItem(SUBSCRIBE_INTENT_KEY, String(Date.now())); } catch { /* sessionStorage unavailable */ }
}
function consumeFreshSubscribeIntent() {
    try {
        const raw = sessionStorage.getItem(SUBSCRIBE_INTENT_KEY);
        if (!raw) return false;
        const ts = parseInt(raw, 10);
        if (!ts || Number.isNaN(ts)) { sessionStorage.removeItem(SUBSCRIBE_INTENT_KEY); return false; }
        if (Date.now() - ts > SUBSCRIBE_INTENT_TTL_MS) {
            sessionStorage.removeItem(SUBSCRIBE_INTENT_KEY);
            return false;
        }
        return true;
    } catch { return false; }
}
function clearSubscribeIntent() {
    try { sessionStorage.removeItem(SUBSCRIBE_INTENT_KEY); } catch { /* sessionStorage unavailable */ }
}

// ── Slim sticky top bar — the way back into the app from /join ─────────────
// Previously /join had no header and no exit (only a /terms link in the dark
// footer), stranding visitors who weren't ready to subscribe. This glass bar is
// pinned to the top so the exit is reachable from anywhere on the page, and it
// gives the marketing page proper brand presence. Context-aware right action:
//   • signed-in (or demo)  → "Browse classes →" into /workouts
//   • signed-out           → "Sign in" (Clerk modal) for returning members
// Wordmark → home (signed-in users get routed to their dashboard automatically).
// Module-scope component (never nested) per project convention; hover via
// useState (never inline DOM mutation).
function JoinHeader({ content, isSignedIn, demoMode }) {
    const navigate = useNavigate();
    const [hover, setHover] = useState(false);
    const showBrowse = isSignedIn || demoMode;
    const browseLabel = (content.joinNavBrowseLabel && content.joinNavBrowseLabel.trim()) || 'Browse classes →';
    const signInLabel = (content.joinNavSignInLabel && content.joinNavSignInLabel.trim()) || 'Sign in';
    const actionStyle = {
        background: 'none', border: 'none', cursor: 'pointer',
        fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600,
        color: hover ? 'var(--mom-primary-dark)' : 'var(--mom-charcoal)',
        padding: '6px 4px', transition: 'color 0.15s ease', whiteSpace: 'nowrap',
    };
    return (
        <header style={{
            position: 'sticky', top: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
            padding: '12px clamp(16px, 4vw, 32px)',
            background: 'rgba(255,255,255,0.72)',
            backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
            borderBottom: '1px solid rgba(0,0,0,0.05)',
        }}>
            <button onClick={() => navigate('/')} aria-label="Moments of Movement — home"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontWeight: 700, fontSize: 'clamp(16px, 2.5vw, 19px)', color: 'var(--mom-charcoal)' }}>
                moments of movement
            </button>
            {showBrowse ? (
                <button onClick={() => navigate('/workouts')} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={actionStyle}>
                    {browseLabel}
                </button>
            ) : (
                <SignInButton mode="modal">
                    <button onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={actionStyle}>
                        {signInLabel}
                    </button>
                </SignInButton>
            )}
        </header>
    );
}

function JoinPage({ demoMode = false }) {
    const navigate = useNavigate();

    // ── Conditional Clerk hooks (matches Account.js pattern) ──
    // demoMode is set at the route level and does not change during a
    // component's lifetime, so conditional hook calls are stable here. In
    // demo mode the ClerkProvider isn't mounted (index.js only mounts it
    // when REACT_APP_CLERK_PUBLISHABLE_KEY is set), so calling these hooks
    // would throw. The try/catch is belt-and-suspenders in case Clerk
    // initialization is racing.
    let isLoaded = true;
    let isSignedIn = false;
    let user = null;
    let getToken = async () => null;
    if (!demoMode) {
        try {
            const u = useUser();
            const a = useAuth();
            isLoaded = u.isLoaded;
            isSignedIn = u.isSignedIn;
            user = u.user;
            getToken = a.getToken;
        } catch (e) { /* fallthrough to default values */ }
    }

    // ── Initial state: cached admin content if available, else DEFAULTS ──
    // `getCachedSiteContent()` reads synchronously from localStorage (populated
    // on any prior page load that resolved a siteConfig fetch). For returning
    // visitors — the typical case for any Insta-link share that's seen
    // traction — this means the very first React paint shows admin's
    // customized copy / photos / CTAs, not the hardcoded defaults. No flash.
    //
    // For absolute first-ever visitors (cache miss), initial state falls back
    // to DEFAULTS, and the useEffect below revalidates async. Brief
    // default-to-admin flash on first visit is unavoidable without SSR — but
    // it's a one-time-per-device cost, not a recurring delay.
    const [content, setContent] = useState(() => {
        const cached = getCachedSiteContent();
        return cached ? mergeContent(cached) : DEFAULTS;
    });
    const [purchaseLoading, setPurchaseLoading] = useState(false);
    const [error, setError] = useState('');

    // ── Yearly plan choice ──
    // `annualOffer` is null unless the admin flag is on AND live Stripe prices
    // loaded and look sane — null means the page is exactly today's page.
    // Yearly is pre-selected (owner decision 2026-09-25); hero + bottom share it.
    const annualOffer = useAnnualOffer(content.enableAnnualPlan === true);
    const [selectedPlan, setSelectedPlan] = useState('annual');
    const planProps = annualOffer ? {
        offer: annualOffer,
        copy: content.planCopy || resolvePlanCopy(),
        value: selectedPlan,
        onChange: setSelectedPlan,
    } : null;

    // ── Paint-first load strategy (conversion-critical) ──
    // No visibility gate. The page tree paints immediately — either with the
    // cached admin content (returning visitors) or DEFAULTS (first-ever
    // visitors). Async siteConfig fetch always runs in the background as
    // stale-while-revalidate; if it returns content that differs from what
    // we painted with, the state updates in place (React re-renders only
    // the changed fields).
    //
    // Why no fade-in here (decision 2026-05-15): /join is the highest-
    // leverage acquisition surface — a shareable URL Danielle posts to
    // Instagram. Every second visitors spend looking at an empty cream
    // screen is a measurable bounce. The prior "wait for siteConfig + RAF
    // ticks then fade in" pattern was suppressing a ~200-500ms first-paint
    // flash at the cost of 2-3 seconds of total time-to-paint. For a
    // conversion page the math is clearly: speed >> flash suppression.
    // The localStorage cache layer added 2026-05-15 closes the flash gap
    // for returning visitors without reintroducing any delay.
    //
    // What this means in practice:
    //   • Returning visitor: instant paint with real admin content (~50ms)
    //   • First-ever visitor: instant paint with DEFAULTS (~50ms), in-place
    //     update to admin content when fetch resolves (~200ms-1s).
    //   • Theme custom-properties (--mom-*) are applied synchronously by
    //     index.js BEFORE React mounts (also seeded from the same cache),
    //     so brand colors are correct on first paint for returning visitors.
    //   • Custom fonts (TAN Ashford / Garuda) follow standard font-display:
    //     swap — system fallback then swap when loaded.
    useEffect(() => {
        let cancelled = false;
        fetchSiteContent().then((c) => {
            if (cancelled) return;
            setContent(mergeContent(c));
        }).catch(() => {
            // Cached content (or DEFAULTS) already in state — silent failure is correct.
        });
        return () => { cancelled = true; };
    }, []);

    // Derived auth state (demo mode short-circuits — no real Clerk, no checkout).
    const clerkUser = demoMode ? null : user;
    const isSubscriber = !demoMode && hasActiveSubscription(clerkUser);
    const userEmail = clerkUser?.primaryEmailAddress?.emailAddress || '';

    // ── Post-auth auto-fire checkout (covers both sign-UP and sign-IN paths) ──
    // The user clicked Subscribe while signed-out; we flipped this ref true AND
    // wrote a sessionStorage timestamp before opening Clerk's modal. Once
    // useUser reflects the post-auth state, this effect fires checkout exactly
    // once. The ref is the fast in-process signal (preferred — wins for sign-up
    // where the modal closes in place). The sessionStorage check is the
    // fallback that survives any component remount Clerk's sign-in flow may
    // trigger (sign-in often navigates through Clerk's default
    // `signInFallbackRedirectUrl`, which can remount /join under us — the ref
    // would then be reset to false but the sessionStorage timestamp persists).
    //
    // Either signal triggers checkout; both are cleared on fire so a later
    // sign-out + sign-in cycle does not re-trigger.
    const justSignedUpRef = useRef(false);

    useEffect(() => {
        if (!isLoaded || !isSignedIn) return;
        // Tier 1: in-process ref (sign-up path — works because modal closes in place).
        // Tier 2: sessionStorage timestamp (sign-in path — survives remount).
        const hasIntent = justSignedUpRef.current || consumeFreshSubscribeIntent();
        if (hasIntent && !isSubscriber && !demoMode) {
            justSignedUpRef.current = false; // one-shot
            clearSubscribeIntent();          // one-shot (both tiers)
            // The plan they picked before Clerk's modal (sessionStorage — the
            // modal may have remounted this page and reset selectedPlan).
            handleSubscribe(consumePlanChoice());
        }
        // handleSubscribe intentionally excluded — it's a stable closure that
        // reads its own current values via getToken() each call. Including it
        // would cause a re-render loop since it's redefined each render.
    }, [isLoaded, isSignedIn, isSubscriber, demoMode]);

    // `storedPlan` is only passed by the post-auth auto-fire. `plan` is sent ONLY
    // when a plan choice was actually shown — otherwise the request body is
    // byte-identical to before the yearly plan existed.
    const handleSubscribe = async (storedPlan) => {
        if (purchaseLoading || demoMode) return;
        setPurchaseLoading(true);
        setError('');
        const plan = storedPlan || (annualOffer ? selectedPlan : null);
        try {
            const token = await getToken();
            const res = await fetch('/api/class-checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    type: 'subscription',
                    userEmail,
                    // Post-checkout lands on the On Demand tab — more depth of
                    // content immediately available than the Schedule tab,
                    // which depends on upcoming class timing.
                    returnTo: 'classes',
                    ...(plan ? { plan } : {}),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.sessionUrl) {
                throw new Error(data.error || "We couldn't start checkout. Please try again.");
            }
            window.location.href = data.sessionUrl;
        } catch (e) {
            setPurchaseLoading(false);
            setError(e.message || 'Something went wrong. Please try again.');
        }
    };

    const handleGoToWorkouts = () => navigate('/workouts');

    return (
        <div style={{
            background: 'var(--mom-cream)',
            minHeight: '100vh',
            position: 'relative',
            zIndex: 1,
            // No opacity gate — paint-first load strategy. See useEffect above
            // for the full rationale (conversion priority over flash suppression).
        }}>
            <div className="mom-atmosphere" />

            <JoinHeader content={content} isSignedIn={isSignedIn} demoMode={demoMode} />

            <HeroSection
                content={content}
                isLoaded={isLoaded}
                isSignedIn={isSignedIn}
                isSubscriber={isSubscriber}
                demoMode={demoMode}
                purchaseLoading={purchaseLoading}
                error={error}
                onSubscribe={handleSubscribe}
                onGoToWorkouts={handleGoToWorkouts}
                justSignedUpRef={justSignedUpRef}
                plan={planProps}
            />

            <IncludedSection content={content} />

            <LifestyleTriptych
                images={[
                    content.joinLifestyle1Image1 || content.joinLifestyleImage1,
                    content.joinLifestyle1Image2,
                    content.joinLifestyle1Image3,
                ]}
                altPrefix="Movement with Danielle"
            />

            <MeetSection content={content} />

            <TestimonialsBlock content={content} />

            <LifestyleTriptych
                images={[
                    content.joinLifestyle2Image1 || content.joinLifestyleImage2,
                    content.joinLifestyle2Image2,
                    content.joinLifestyle2Image3,
                ]}
                altPrefix="Real life, real movement"
            />

            <StatsSection content={content} />

            <FaqSection content={content} />

            <BottomCtaSection
                content={content}
                isLoaded={isLoaded}
                isSignedIn={isSignedIn}
                isSubscriber={isSubscriber}
                demoMode={demoMode}
                purchaseLoading={purchaseLoading}
                onSubscribe={handleSubscribe}
                onGoToWorkouts={handleGoToWorkouts}
                justSignedUpRef={justSignedUpRef}
                plan={planProps}
            />

            <Footer />
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  HERO
// ══════════════════════════════════════════════════════════════════════
function HeroSection({
    content, isLoaded, isSignedIn, isSubscriber, demoMode,
    purchaseLoading, error, onSubscribe, onGoToWorkouts, justSignedUpRef, plan,
}) {
    // Multi-line headline support: \n in admin field → soft break
    const headlineLines = (content.joinHeroHeadline || '').split(/\n/);

    return (
        <section style={{
            position: 'relative',
            padding: 'clamp(48px, 8vw, 96px) clamp(20px, 5vw, 32px) clamp(48px, 6vw, 72px)',
            zIndex: 2,
        }}>
            <div style={{
                maxWidth: 1100,
                margin: '0 auto',
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr)',
                gap: 'clamp(24px, 4vw, 56px)',
                alignItems: 'center',
            }}
                // Mid-breakpoint: two-column when there's a hero photo + viewport ≥ 900px.
                // Single-column always when no photo, or on narrower viewports — handled
                // via the className-driven media query at the bottom of this file.
                className={content.joinHeroImage ? 'mom-join-hero-grid' : ''}
            >
                {/* Text column */}
                <div>
                    {/* Eyebrow — small caps brand kicker */}
                    {content.joinHeroEyebrow && (
                        <p style={{
                            fontFamily: 'var(--mom-font-sans)',
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.22em',
                            textTransform: 'uppercase',
                            color: 'var(--mom-primary)',
                            marginBottom: 18,
                        }}>
                            {content.joinHeroEyebrow}
                        </p>
                    )}

                    {/* Headline */}
                    <h1 style={{
                        fontFamily: 'var(--mom-font-serif)',
                        fontSize: 'clamp(36px, 6vw, 64px)',
                        fontStyle: 'italic',
                        color: 'var(--mom-charcoal)',
                        lineHeight: 1.05,
                        letterSpacing: '-0.02em',
                        marginBottom: 20,
                    }}>
                        {headlineLines.map((line, i) => (
                            <React.Fragment key={i}>
                                {line}
                                {i < headlineLines.length - 1 && <br />}
                            </React.Fragment>
                        ))}
                    </h1>

                    {/* Sub-copy */}
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)',
                        fontSize: 17,
                        color: 'var(--mom-charcoal)',
                        opacity: 0.85,
                        lineHeight: 1.7,
                        maxWidth: 540,
                        marginBottom: 32,
                    }}>
                        {content.joinHeroSubcopy}
                    </p>

                    {/* Primary CTA + reassurance */}
                    <CtaButton
                        content={content}
                        isLoaded={isLoaded}
                        isSignedIn={isSignedIn}
                        isSubscriber={isSubscriber}
                        demoMode={demoMode}
                        purchaseLoading={purchaseLoading}
                        onSubscribe={onSubscribe}
                        onGoToWorkouts={onGoToWorkouts}
                        justSignedUpRef={justSignedUpRef}
                        plan={plan}
                        variant="hero"
                    />

                    {error && (
                        <p style={{
                            fontFamily: 'var(--mom-font-sans)',
                            fontSize: 13,
                            color: 'var(--mom-danger, #e53e3e)',
                            marginTop: 12,
                            maxWidth: 360,
                        }}>
                            {error}
                        </p>
                    )}

                    {/* Social proof one-liner */}
                    {content.joinSocialProof && (
                        <p style={{
                            fontFamily: 'var(--mom-font-serif)',
                            fontStyle: 'italic',
                            fontSize: 15,
                            color: 'var(--mom-warm-gray)',
                            marginTop: 28,
                        }}>
                            {content.joinSocialProof}
                        </p>
                    )}
                </div>

                {/* Photo column */}
                {content.joinHeroImage && (
                    <div style={{ position: 'relative' }}>
                        <div style={{
                            position: 'relative',
                            aspectRatio: '4/5',
                            borderRadius: 24,
                            overflow: 'hidden',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
                        }}>
                            <img
                                src={content.joinHeroImage}
                                alt=""
                                style={{
                                    width: '100%', height: '100%',
                                    objectFit: 'cover', display: 'block',
                                }}
                                referrerPolicy="no-referrer"
                            />
                            {/* Specular highlight — premium glass feel */}
                            <div style={{
                                position: 'absolute', top: 0, left: 0, right: 0,
                                height: '40%',
                                background: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 100%)',
                                pointerEvents: 'none',
                            }} />
                            {/* Subtle inner border */}
                            <div style={{
                                position: 'absolute', inset: 0,
                                borderRadius: 24,
                                border: '1px solid rgba(255,255,255,0.4)',
                                pointerEvents: 'none',
                            }} />
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  CTA BUTTON — branches by auth state
// ══════════════════════════════════════════════════════════════════════
function CtaButton({
    content, isLoaded, isSignedIn, isSubscriber, demoMode,
    purchaseLoading, onSubscribe, onGoToWorkouts, justSignedUpRef, variant, plan,
}) {
    // variant: 'hero' (primary gradient on cream) or 'bottom' (white on gradient)
    const isBottom = variant === 'bottom';

    const buttonBase = {
        padding: '16px 36px',
        borderRadius: 14,
        fontSize: 16,
        fontWeight: 700,
        fontFamily: 'var(--mom-font-sans)',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s',
        whiteSpace: 'nowrap',
        boxShadow: isBottom
            ? '0 4px 16px rgba(0,0,0,0.18)'
            : '0 4px 16px color-mix(in srgb, var(--mom-primary) 24%, transparent)',
    };

    const buttonPrimary = {
        ...buttonBase,
        background: isBottom
            ? 'var(--mom-white)'
            : 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
        color: isBottom ? 'var(--mom-primary-dark)' : '#fff',
    };

    const buttonSecondary = {
        ...buttonBase,
        background: 'transparent',
        color: isBottom ? '#fff' : 'var(--mom-charcoal)',
        border: `1.5px solid ${isBottom ? 'rgba(255,255,255,0.7)' : 'var(--mom-cream-dark)'}`,
        boxShadow: 'none',
    };

    const reassuranceStyle = {
        fontFamily: 'var(--mom-font-sans)',
        fontSize: 12,
        color: isBottom ? 'rgba(255,255,255,0.85)' : 'var(--mom-warm-gray)',
        marginTop: 12,
    };

    const priceChipStyle = {
        fontFamily: 'var(--mom-font-sans)',
        fontSize: 13,
        fontWeight: 600,
        color: isBottom ? 'rgba(255,255,255,0.9)' : 'var(--mom-warm-gray)',
        // No marginLeft — the CTA row is now column-stacked, the parent's
        // flex `gap` handles vertical spacing between button and price.
    };

    // ── Row layout ──
    // Always column-stacked: button on top, price below. Variant decides
    // alignment — hero is left-aligned (editorial composition matches the
    // left-aligned headline above it), bottom is centered (matches the
    // center-aligned brand-gradient section). Mobile override (in JOIN_STYLES)
    // switches to `align-items: stretch` so the button hits 100% width.
    const ctaRowStyle = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: isBottom ? 'center' : 'flex-start',
        gap: 10,
    };

    // ── Yearly plan choice (docs/ux/annual-plan.md) ──
    // When `plan` is set, the two plan cards sit ABOVE the button and replace
    // the price chip below it (the cards carry both prices). When it's null,
    // `planBlock` is null and `priceChip` is exactly the chip this page always
    // rendered — so every branch below is unchanged with the feature off.
    const planBlock = plan ? (
        <div style={{ width: '100%', display: 'flex', justifyContent: isBottom ? 'center' : 'flex-start', marginBottom: 8 }}>
            <PlanOptions
                offer={plan.offer}
                copy={plan.copy}
                value={plan.value}
                onChange={plan.onChange}
                tone={isBottom ? 'dark' : 'light'}
            />
        </div>
    ) : null;
    const priceChip = plan ? null : (
        <span style={priceChipStyle}>{content.subscriptionPriceLabel || '$15/mo'}</span>
    );

    // ── Order note ──
    // Subscriber branch lifted to the top so existing subscribers always see
    // "You're already in" regardless of admin's enrollment toggle. Closing
    // enrollment does NOT revoke their access — they keep their account.
    //
    // The closed-state gate runs immediately after, replacing every other
    // Subscribe path (not-loaded placeholder, demo, signed-in non-sub,
    // signed-out) with the editorial enrollment-opens message. Falsy-strict
    // `=== false` so the brief pre-fetch window (subscriptionEnabled
    // undefined) falls through to the normal CTAs — matches the
    // `siteConfig.subscriptionEnabled !== false` pattern in Workouts.js.

    // ── Signed-in subscriber — editorial member view (always shown) ──
    // Replaces the prior outline-button + redundant text-link pattern with a
    // composition that matches the page's editorial voice: small-caps
    // eyebrow, italic-serif welcome headline, optional sub-copy, single
    // filled primary button. Variant decides alignment + colors (hero is
    // left-aligned with brand-gradient button; bottom is centered with
    // white-on-gradient button — both use the shared `buttonPrimary` style).
    if (isLoaded && isSignedIn && isSubscriber) {
        const memberEyebrow = content.joinMemberEyebrow;
        const memberHeadline = content.joinMemberHeadline || DEFAULTS.joinMemberHeadline;
        const memberSubcopy = content.joinMemberSubcopy;
        const memberCta = content.joinMemberCta || DEFAULTS.joinMemberCta;

        // textAlign + margin handling lets us slot the text block into both
        // the left-aligned hero column AND the centered bottom-CTA section
        // without changing the container structure that wraps CtaButton.
        const memberTextAlign = isBottom ? 'center' : 'left';
        const memberBlockCenter = isBottom ? { marginLeft: 'auto', marginRight: 'auto' } : {};

        return (
            <div>
                {memberEyebrow && (
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)',
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.22em',
                        textTransform: 'uppercase',
                        color: isBottom ? 'rgba(255,255,255,0.9)' : 'var(--mom-primary)',
                        margin: '0 0 14px 0',
                        textAlign: memberTextAlign,
                    }}>
                        {memberEyebrow}
                    </p>
                )}
                <p style={{
                    fontFamily: 'var(--mom-font-serif)',
                    fontStyle: 'italic',
                    fontSize: 'clamp(22px, 3vw, 30px)',
                    color: isBottom ? '#ffffff' : 'var(--mom-charcoal)',
                    lineHeight: 1.25,
                    letterSpacing: '-0.01em',
                    margin: '0 0 16px 0',
                    maxWidth: 480,
                    textAlign: memberTextAlign,
                    ...memberBlockCenter,
                }}>
                    {memberHeadline}
                </p>
                {memberSubcopy && memberSubcopy.trim() && (
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)',
                        fontSize: 15,
                        color: isBottom ? 'rgba(255,255,255,0.85)' : 'var(--mom-charcoal)',
                        opacity: isBottom ? 1 : 0.85,
                        lineHeight: 1.5,
                        margin: '0 0 24px 0',
                        maxWidth: 440,
                        textAlign: memberTextAlign,
                        ...memberBlockCenter,
                    }}>
                        {memberSubcopy}
                    </p>
                )}
                <div className="mom-join-cta-row" style={ctaRowStyle}>
                    <button style={buttonPrimary} onClick={onGoToWorkouts}>
                        {memberCta}
                    </button>
                </div>
            </div>
        );
    }

    // ── Enrollment closed — admin toggle off — replace Subscribe CTA ──
    // Editorial italic-serif treatment, no button. Variant decides color +
    // text alignment (left for hero, centered for bottom — matches parent
    // section composition). Subscribers never reach this branch (caught above).
    if (content.subscriptionEnabled === false) {
        return (
            <div className="mom-join-cta-row" style={ctaRowStyle}>
                <p style={{
                    fontFamily: 'var(--mom-font-serif)',
                    fontStyle: 'italic',
                    fontSize: 'clamp(20px, 2.6vw, 26px)',
                    fontWeight: 400,
                    color: isBottom ? '#ffffff' : 'var(--mom-charcoal)',
                    lineHeight: 1.4,
                    letterSpacing: '-0.01em',
                    margin: 0,
                    maxWidth: 560,
                    textAlign: isBottom ? 'center' : 'left',
                }}>
                    {content.joinEnrollmentClosedMessage || DEFAULTS.joinEnrollmentClosedMessage}
                </p>
            </div>
        );
    }

    // ── Not loaded — disabled placeholder ──
    if (!isLoaded && !demoMode) {
        return (
            <div className="mom-join-cta-row" style={ctaRowStyle}>
                {planBlock}
                <button disabled style={{ ...buttonPrimary, opacity: 0.5, cursor: 'default' }}>
                    {content.joinPrimaryCta || DEFAULTS.joinPrimaryCta}
                </button>
                {priceChip}
            </div>
        );
    }

    // ── Demo mode — no-op styled button ──
    if (demoMode) {
        return (
            <div>
                <div className="mom-join-cta-row" style={ctaRowStyle}>
                    {planBlock}
                    <button style={buttonPrimary} onClick={() => { /* demo no-op */ }}>
                        {content.joinPrimaryCta || DEFAULTS.joinPrimaryCta}
                    </button>
                    {priceChip}
                </div>
                <p className="mom-join-reassurance" style={reassuranceStyle}>{content.joinReassurance}</p>
            </div>
        );
    }

    // ── Signed-in, not subscribed — direct checkout ──
    if (isSignedIn && !isSubscriber) {
        return (
            <div>
                <div className="mom-join-cta-row" style={ctaRowStyle}>
                    {planBlock}
                    <button
                        style={{ ...buttonPrimary, opacity: purchaseLoading ? 0.6 : 1 }}
                        onClick={() => onSubscribe()}
                        disabled={purchaseLoading}
                    >
                        {purchaseLoading ? 'Redirecting…' : (content.joinPrimaryCta || DEFAULTS.joinPrimaryCta)}
                    </button>
                    {priceChip}
                </div>
                <p className="mom-join-reassurance" style={reassuranceStyle}>{content.joinReassurance}</p>
            </div>
        );
    }

    // ── Signed-out — Clerk modal sign-up; auto-fires checkout once signed in ──
    //
    // ── Returning-member discoverability in the sign-up modal ──
    // Clerk's default modal renders "Already have an account? Sign in" as a small
    // inline link in the footer — easy to miss for returning members who don't
    // realize they need to switch off the sign-up tab. The global `clerkAppearance`
    // (index.js) already lifts the link contrast (brand pink-dark + bold + underline)
    // — that was the WCAG legibility pass. This per-instance `appearance` goes
    // further: it escalates the link from "styled inline link" to "obvious outline-
    // button secondary CTA" with a divider above the footer block, so a returning
    // member's eye lands on it immediately.
    //
    // Scope: this `appearance` prop applies ONLY to this SignUpButton instance.
    // It deep-merges with the global `clerkAppearance` at modal render time, so
    // every other Clerk modal in the app (LandingPage SignUp/SignIn, Clerk's
    // built-in account/security flows, Account-page Clerk surfaces) is unaffected.
    // The `&:hover` selector is Clerk-CSS-in-JS: applied to a real `:hover` state
    // when their internal style system renders the element.
    //
    // Why no localization override: Clerk's `localization` prop is a ClerkProvider-
    // global setting and does NOT accept per-instance scope. Overriding the copy
    // here would change "Already have an account? Sign in" everywhere — including
    // LandingPage, which already has a separate Sign In button on the page and
    // doesn't need the change. We accepted the default copy and put all the
    // discoverability lift into visual treatment.
    //
    // Auto-fire still works for the sign-in path: when a returning user clicks the
    // restyled "Sign in" footer CTA, Clerk swaps the modal to its sign-in form;
    // `justSignedUpRef.current` was set to true the moment they clicked
    // "Start Moving", so the post-auth effect (see useEffect on isLoaded/isSignedIn
    // above) fires `handleSubscribe()` regardless of whether they signed up or
    // signed in. Zero extra clicks compared to today.
    const signUpModalAppearance = {
        elements: {
            // Tight footer: no top border, no extra top spacing — the footer
            // block reads as a continuation of the form, not a separate
            // section. Per design feedback (2026-05-15): the prior divider +
            // padding made the prompt feel like a footnote rather than a
            // natural next-step.
            footer: {
                borderTop: 'none',
                paddingTop: 0,
                marginTop: 0,
            },
            // Row container — center, tight gap.
            footerAction: {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                flexWrap: 'wrap',
                paddingTop: 0,
            },
            // Prompt text ("Already have an account?") — matches the modal's
            // own headerTitle ("Create your account") in font, color, and
            // weight per design feedback (2026-05-15). Italic serif, charcoal,
            // weight 700. Size kept smaller than the actual title (~16px) so
            // it reads as a peer-level subhead rather than competing with the
            // title for primary attention.
            footerActionText: {
                fontFamily: 'var(--mom-font-serif)',
                fontStyle: 'italic',
                fontWeight: 700,
                fontSize: 16,
                color: '#2d2926',
            },
            // Footer link styled as a FILLED primary-button — same brand-pink
            // gradient + white text + shadow as the modal's "Continue" CTA
            // (see `formButtonPrimary` in src/index.js global appearance).
            // The Sign In CTA now reads as visually equal in weight to the
            // signup form's submit — explicit invitation, not a secondary
            // option. Per design feedback (2026-05-15).
            footerActionLink: {
                display: 'inline-block',
                padding: '10px 22px',
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'var(--mom-font-sans)',
                color: '#ffffff',
                background: 'linear-gradient(135deg, #c33764, #a02a52)',
                border: 'none',
                borderRadius: 14,
                textDecoration: 'none',
                transition: 'all 0.2s',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(195, 55, 100, 0.35)',
                '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 18px rgba(195, 55, 100, 0.45)',
                },
            },
        },
    };

    return (
        <div>
            <div className="mom-join-cta-row" style={ctaRowStyle}>
                {planBlock}
                <SignUpButton mode="modal" appearance={signUpModalAppearance}>
                    <button
                        style={buttonPrimary}
                        onClick={() => {
                            // Set BOTH intent signals — the fast in-process ref
                            // (preferred for the sign-up path) and the persistent
                            // sessionStorage timestamp (fallback that survives
                            // any remount Clerk's sign-in flow may trigger). See
                            // the post-auth useEffect in JoinPage for the
                            // consumer side. Either signal alone is sufficient;
                            // both being set is the belt-and-suspenders default.
                            justSignedUpRef.current = true;
                            markSubscribeIntent();
                            // Carry the chosen plan across Clerk's modal.
                            if (plan) rememberPlanChoice(plan.value);
                        }}
                    >
                        {content.joinPrimaryCta || DEFAULTS.joinPrimaryCta}
                    </button>
                </SignUpButton>
                {priceChip}
            </div>
            <p className="mom-join-reassurance" style={reassuranceStyle}>{content.joinReassurance}</p>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  WHAT'S INCLUDED
// ══════════════════════════════════════════════════════════════════════
function IncludedSection({ content }) {
    const items = [];
    for (let i = 1; i <= 6; i++) {
        const title = content[`joinIncluded${i}Title`];
        if (title && title.trim()) {
            items.push({
                title,
                desc: content[`joinIncluded${i}Desc`] || '',
            });
        }
    }
    if (items.length === 0) return null;

    return (
        <section style={{
            padding: 'clamp(56px, 8vw, 96px) clamp(20px, 5vw, 32px)',
            background: 'var(--mom-white)',
            position: 'relative',
            zIndex: 2,
        }}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: 48 }}>
                    <h2 style={{
                        fontFamily: 'var(--mom-font-serif)',
                        fontSize: 'clamp(28px, 4vw, 40px)',
                        fontStyle: 'italic',
                        color: 'var(--mom-charcoal)',
                        marginBottom: 12,
                        letterSpacing: '-0.01em',
                    }}>
                        {content.joinIncludedHeading}
                    </h2>
                    {content.joinIncludedSubheading && (
                        <p style={{
                            fontFamily: 'var(--mom-font-sans)',
                            fontSize: 16,
                            color: 'var(--mom-warm-gray)',
                        }}>
                            {content.joinIncludedSubheading}
                        </p>
                    )}
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: 20,
                }}>
                    {items.map((item, i) => (
                        // Numeral is auto-generated from display position (renumbers
                        // when admin hides a tile — always reads as a complete sequence,
                        // never "01, 02, 04"). Zero-padded for editorial polish.
                        <IncludedTile
                            key={i}
                            number={String(i + 1).padStart(2, '0')}
                            title={item.title}
                            desc={item.desc}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}

function IncludedTile({ number, title, desc }) {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                background: 'linear-gradient(180deg, var(--mom-tile-bg, #ffffff) 0%, #fbfcfd 40%, #f6f8fa 100%)',
                borderRadius: 20,
                padding: '32px 28px',
                position: 'relative',
                overflow: 'hidden',
                transform: hovered ? 'translateY(-4px) scale(1.01)' : 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: hovered
                    ? '0 12px 32px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)'
                    : '0 4px 16px rgba(0,0,0,0.05), 0 2px 6px rgba(0,0,0,0.03)',
            }}
        >
            {/* Specular highlight */}
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0,
                height: '45%',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.7) 0%, transparent 100%)',
                pointerEvents: 'none',
                borderRadius: '20px 20px 0 0',
            }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
                {/* Editorial numeral — italic serif, brand color, large.
                    Matches the italic-serif headline language used across the
                    brand (TermsPage h1, hero headlines, testimonials section
                    heading). Tight line-height + negative letter-spacing keep
                    it feeling intentional rather than counter-like. */}
                {number && (
                    <div style={{
                        fontFamily: 'var(--mom-font-serif)',
                        fontStyle: 'italic',
                        fontSize: 'clamp(40px, 4vw, 48px)',
                        fontWeight: 700,
                        color: 'var(--mom-primary)',
                        lineHeight: 1,
                        letterSpacing: '-0.04em',
                        marginBottom: 20,
                    }}>
                        {number}
                    </div>
                )}
                <h3 style={{
                    fontFamily: 'var(--mom-font-sans)',
                    fontSize: 17,
                    fontWeight: 700,
                    color: 'var(--mom-charcoal)',
                    marginBottom: 10,
                    letterSpacing: '-0.005em',
                    lineHeight: 1.3,
                }}>
                    {title}
                </h3>
                <p style={{
                    fontFamily: 'var(--mom-font-sans)',
                    fontSize: 14,
                    color: 'var(--mom-warm-gray)',
                    lineHeight: 1.6,
                }}>
                    {desc}
                </p>
            </div>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  LIFESTYLE TRIPTYCH — three magazine-style portrait tiles
// ══════════════════════════════════════════════════════════════════════
// Three 4:5 portrait tiles. Desktop renders as a three-column triptych
// (magazine spread feel). Mobile stacks single-column. Aspect ratio is
// constant across viewports so admin's chosen crop survives the breakpoint
// — Instagram-standard 4:5 means most photos admin uploads from her
// camera roll are already the right shape.
//
// If admin fills fewer than 3 slots, the grid auto-fits to the available
// images — empty slots collapse, no awkward gaps. Section hides entirely
// if all slots are empty. Backwards-compat: parent passes the legacy
// `joinLifestyleImage1/2` as the first slot if the new key is empty.
function LifestyleTriptych({ images, altPrefix }) {
    const filtered = (images || []).filter(Boolean);
    if (filtered.length === 0) return null;

    return (
        <section style={{
            position: 'relative',
            background: 'var(--mom-cream)',
            padding: 'clamp(32px, 5vw, 56px) clamp(20px, 5vw, 32px)',
            zIndex: 2,
        }}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                <div
                    className="mom-join-lifestyle-grid"
                    style={{
                        display: 'grid',
                        // Auto-fit means: 1 image → 1 column, 2 images → 2 columns,
                        // 3 images → 3 columns. Below ~540px viewport, always 1
                        // column (the minmax min-width kicks in). Explicit media
                        // query in JOIN_STYLES locks 3 columns at ≥700px so
                        // tablet-width triptychs read as intended.
                        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                        gap: 'clamp(12px, 2vw, 18px)',
                    }}
                >
                    {filtered.map((src, i) => (
                        <LifestyleTile
                            key={`${src}-${i}`}
                            src={src}
                            alt={altPrefix ? `${altPrefix} ${i + 1}` : ''}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}

function LifestyleTile({ src, alt }) {
    return (
        <div
            className="mom-join-lifestyle-tile"
            style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '4 / 5',
                borderRadius: 20,
                overflow: 'hidden',
                boxShadow: '0 8px 28px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
            }}
        >
            <img
                src={src}
                alt={alt}
                style={{
                    width: '100%', height: '100%',
                    objectFit: 'cover', display: 'block',
                }}
                referrerPolicy="no-referrer"
            />
            {/* Specular highlight at top — matches Hero photo treatment for
                premium glass feel. Subtle; doesn't fight the underlying photo. */}
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0,
                height: '35%',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, transparent 100%)',
                pointerEvents: 'none',
                borderRadius: '20px 20px 0 0',
            }} />
            {/* 1px inner border — adds depth/definition over the photo edge. */}
            <div style={{
                position: 'absolute', inset: 0,
                borderRadius: 20,
                border: '1px solid rgba(255,255,255,0.3)',
                pointerEvents: 'none',
            }} />
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  MEET DANIELLE
// ══════════════════════════════════════════════════════════════════════
function MeetSection({ content }) {
    if (!content.joinMeetHeading && !content.joinMeetBody) return null;

    return (
        <section style={{
            padding: 'clamp(56px, 8vw, 96px) clamp(20px, 5vw, 32px)',
            background: 'var(--mom-cream)',
            position: 'relative',
            zIndex: 2,
        }}>
            <div
                className={content.joinMeetImage ? 'mom-join-meet-grid' : ''}
                style={{
                    maxWidth: 900,
                    margin: '0 auto',
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr)',
                    gap: 'clamp(24px, 4vw, 48px)',
                    alignItems: 'center',
                }}
            >
                {content.joinMeetImage && (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <div className="mom-join-meet-photo" style={{
                            width: 220,
                            height: 220,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            border: '3px solid var(--mom-cream-dark)',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                            flexShrink: 0,
                        }}>
                            <img
                                src={content.joinMeetImage}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                referrerPolicy="no-referrer"
                            />
                        </div>
                    </div>
                )}
                <div>
                    {content.joinMeetHeading && (
                        <h2 style={{
                            fontFamily: 'var(--mom-font-serif)',
                            fontSize: 'clamp(28px, 4vw, 38px)',
                            fontStyle: 'italic',
                            color: 'var(--mom-charcoal)',
                            marginBottom: 20,
                            letterSpacing: '-0.01em',
                        }}>
                            {content.joinMeetHeading}
                        </h2>
                    )}
                    {content.joinMeetBody && (
                        <FormattedText
                            text={content.joinMeetBody}
                            style={{
                                fontFamily: 'var(--mom-font-sans)',
                                fontSize: 16,
                                color: 'var(--mom-charcoal)',
                                lineHeight: 1.7,
                            }}
                        />
                    )}
                </div>
            </div>
        </section>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  TESTIMONIALS
// ══════════════════════════════════════════════════════════════════════
function TestimonialsBlock({ content }) {
    const pool = resolveTestimonials(content);
    // Membership tag: /join sells the membership, so it should only carry quotes
    // about the membership — not program-specific ones from the shared pool.
    // Strict once adopted: as soon as ANY testimonial is tagged `membership`,
    // only tagged ones render here. Until the first tag exists the whole pool
    // renders exactly as before, so shipping this can never empty the section.
    const tagged = pool.filter((t) => t.membership === true);
    const all = tagged.length > 0 ? tagged : pool;
    const [showAll, setShowAll] = useState(false);

    if (all.length === 0) return null;
    const displayed = all.slice(0, 5);
    const hasMore = all.length > 5;

    return (
        <section style={{
            padding: 'clamp(56px, 8vw, 96px) clamp(20px, 5vw, 32px)',
            background: 'var(--mom-white)',
            position: 'relative',
            zIndex: 2,
        }}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: 48 }}>
                    <h2 style={{
                        fontFamily: 'var(--mom-font-serif)',
                        fontSize: 'clamp(28px, 4vw, 40px)',
                        fontStyle: 'italic',
                        color: 'var(--mom-charcoal)',
                        marginBottom: 12,
                        letterSpacing: '-0.01em',
                    }}>
                        {content.joinTestimonialsHeading}
                    </h2>
                    {content.joinTestimonialsSubheading && (
                        <p style={{
                            fontFamily: 'var(--mom-font-sans)',
                            fontSize: 16,
                            color: 'var(--mom-warm-gray)',
                        }}>
                            {content.joinTestimonialsSubheading}
                        </p>
                    )}
                </div>

                <TestimonialsGrid testimonials={displayed} />

                {hasMore && (
                    <div style={{ textAlign: 'center', marginTop: 32 }}>
                        <button
                            onClick={() => setShowAll(true)}
                            style={{
                                fontFamily: 'var(--mom-font-sans)',
                                fontSize: 15,
                                fontWeight: 600,
                                color: 'var(--mom-link)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '8px 0',
                            }}
                        >
                            See all testimonials →
                        </button>
                    </div>
                )}
            </div>

            {showAll && (
                <AllTestimonialsModal testimonials={all} onClose={() => setShowAll(false)} />
            )}
        </section>
    );
}

function TestimonialsGrid({ testimonials }) {
    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 20,
        }}>
            {testimonials.map((t, i) => (
                <TestimonialCard key={i} testimonial={t} />
            ))}
        </div>
    );
}

function TestimonialCard({ testimonial }) {
    const t = testimonial;
    // Initial-letter avatar like LandingPage
    const initial = (t.name || '?').trim().charAt(0).toUpperCase();
    return (
        <div style={{
            background: 'var(--mom-cream)',
            borderRadius: 18,
            padding: '28px 26px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
        }}>
            {/* Quote mark */}
            <span style={{
                position: 'absolute',
                top: 8,
                left: 14,
                fontSize: 48,
                fontFamily: 'var(--mom-font-serif)',
                fontStyle: 'italic',
                color: 'color-mix(in srgb, var(--mom-primary) 18%, transparent)',
                lineHeight: 1,
                pointerEvents: 'none',
            }}>
                "
            </span>
            <p style={{
                fontFamily: 'var(--mom-font-sans)',
                fontSize: 15,
                color: 'var(--mom-charcoal)',
                lineHeight: 1.7,
                position: 'relative',
                zIndex: 1,
                flex: 1,
            }}>
                {t.text}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--mom-primary-light), var(--mom-primary))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontFamily: 'var(--mom-font-serif)',
                    fontStyle: 'italic',
                    fontWeight: 700,
                    fontSize: 17,
                    flexShrink: 0,
                }}>
                    {initial}
                </div>
                <div style={{ minWidth: 0 }}>
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)',
                        fontSize: 13,
                        fontWeight: 700,
                        color: 'var(--mom-charcoal)',
                    }}>
                        {t.name || 'Anonymous'}
                    </p>
                    {t.detail && (
                        <p style={{
                            fontFamily: 'var(--mom-font-sans)',
                            fontSize: 12,
                            color: 'var(--mom-warm-gray)',
                        }}>
                            {t.detail}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

function AllTestimonialsModal({ testimonials, onClose }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 20,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: 'var(--mom-white)',
                    borderRadius: 20,
                    padding: 'clamp(24px, 4vw, 40px)',
                    maxWidth: 900,
                    width: '100%',
                    maxHeight: '85vh',
                    overflowY: 'auto',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <h3 style={{
                        fontFamily: 'var(--mom-font-serif)',
                        fontSize: 24,
                        fontStyle: 'italic',
                        color: 'var(--mom-charcoal)',
                    }}>
                        All testimonials
                    </h3>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: 22, color: 'var(--mom-warm-gray)',
                            padding: 4, lineHeight: 1,
                        }}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                <TestimonialsGrid testimonials={testimonials} />
            </div>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  STATS / SOCIAL PROOF
// ══════════════════════════════════════════════════════════════════════
function StatsSection({ content }) {
    const stats = [
        { num: content.joinStat1Num, label: content.joinStat1Label },
        { num: content.joinStat2Num, label: content.joinStat2Label },
        { num: content.joinStat3Num, label: content.joinStat3Label },
    ].filter((s) => s.num && String(s.num).trim());

    if (stats.length === 0) return null;

    return (
        <section style={{
            padding: 'clamp(56px, 8vw, 96px) clamp(20px, 5vw, 32px)',
            background: 'var(--mom-cream)',
            position: 'relative',
            zIndex: 2,
        }}>
            <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
                {content.joinStatsHeading && (
                    <h2 style={{
                        fontFamily: 'var(--mom-font-serif)',
                        fontSize: 'clamp(24px, 3.5vw, 32px)',
                        fontStyle: 'italic',
                        color: 'var(--mom-charcoal)',
                        marginBottom: 40,
                        letterSpacing: '-0.01em',
                    }}>
                        {content.joinStatsHeading}
                    </h2>
                )}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 'clamp(32px, 6vw, 80px)',
                    flexWrap: 'wrap',
                }}>
                    {stats.map((s, i) => (
                        <div key={i} style={{ textAlign: 'center', minWidth: 100 }}>
                            <div style={{
                                fontFamily: 'var(--mom-font-serif)',
                                fontSize: 'clamp(40px, 6vw, 56px)',
                                fontStyle: 'italic',
                                fontWeight: 700,
                                color: 'var(--mom-primary)',
                                lineHeight: 1,
                                letterSpacing: '-0.02em',
                            }}>
                                {s.num}
                            </div>
                            <div style={{
                                marginTop: 10,
                                fontFamily: 'var(--mom-font-sans)',
                                fontSize: 13,
                                fontWeight: 600,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                                color: 'var(--mom-warm-gray)',
                            }}>
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  FAQ
// ══════════════════════════════════════════════════════════════════════
function FaqSection({ content }) {
    const items = [];
    // 10 slots total (5 with defaults + 5 optional overflow). Each row only
    // renders if BOTH Q and A are populated — half-filled rows silently hide.
    for (let i = 1; i <= 10; i++) {
        const q = content[`joinFaq${i}Q`];
        const a = content[`joinFaq${i}A`];
        if (q && q.trim() && a && a.trim()) {
            items.push({ q, a });
        }
    }
    if (items.length === 0) return null;

    return (
        <section style={{
            padding: 'clamp(56px, 8vw, 96px) clamp(20px, 5vw, 32px)',
            background: 'var(--mom-white)',
            position: 'relative',
            zIndex: 2,
        }}>
            <div style={{ maxWidth: 760, margin: '0 auto' }}>
                {content.joinFaqHeading && (
                    <h2 style={{
                        fontFamily: 'var(--mom-font-serif)',
                        fontSize: 'clamp(28px, 4vw, 40px)',
                        fontStyle: 'italic',
                        color: 'var(--mom-charcoal)',
                        textAlign: 'center',
                        marginBottom: 40,
                        letterSpacing: '-0.01em',
                    }}>
                        {content.joinFaqHeading}
                    </h2>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {items.map((item, i) => (
                        <FaqItem key={i} q={item.q} a={item.a} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function FaqItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div style={{
            background: 'var(--mom-cream)',
            borderRadius: 14,
            overflow: 'hidden',
            border: '1px solid var(--mom-cream-dark)',
        }}>
            <button
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                style={{
                    width: '100%',
                    padding: '18px 22px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 16,
                    textAlign: 'left',
                    fontFamily: 'var(--mom-font-sans)',
                    fontSize: 16,
                    fontWeight: 600,
                    color: 'var(--mom-charcoal)',
                }}
            >
                <span style={{ flex: 1 }}>{q}</span>
                <span style={{
                    fontSize: 16,
                    color: 'var(--mom-warm-gray)',
                    transition: 'transform 0.2s',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    flexShrink: 0,
                }}>
                    ▾
                </span>
            </button>
            {open && (
                <div style={{
                    padding: '0 22px 20px',
                    fontFamily: 'var(--mom-font-sans)',
                    fontSize: 15,
                    color: 'var(--mom-charcoal)',
                    opacity: 0.88,
                    lineHeight: 1.7,
                    whiteSpace: 'pre-line',
                }}>
                    {a}
                </div>
            )}
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  BOTTOM CTA — full-bleed brand gradient
// ══════════════════════════════════════════════════════════════════════
function BottomCtaSection({
    content, isLoaded, isSignedIn, isSubscriber, demoMode,
    purchaseLoading, onSubscribe, onGoToWorkouts, justSignedUpRef, plan,
}) {
    return (
        <section style={{
            padding: 'clamp(64px, 9vw, 120px) clamp(20px, 5vw, 32px)',
            background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
            position: 'relative',
            zIndex: 2,
            textAlign: 'center',
        }}>
            <div style={{ maxWidth: 720, margin: '0 auto' }}>
                <h2 style={{
                    fontFamily: 'var(--mom-font-serif)',
                    fontSize: 'clamp(32px, 5vw, 48px)',
                    fontStyle: 'italic',
                    color: '#fff',
                    marginBottom: 16,
                    letterSpacing: '-0.015em',
                    lineHeight: 1.15,
                }}>
                    {content.joinBottomHeadline}
                </h2>
                {content.joinBottomSubcopy && (
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)',
                        fontSize: 17,
                        color: 'rgba(255,255,255,0.9)',
                        lineHeight: 1.7,
                        marginBottom: 36,
                        maxWidth: 560,
                        marginLeft: 'auto',
                        marginRight: 'auto',
                    }}>
                        {content.joinBottomSubcopy}
                    </p>
                )}
                {/* Wrapper: flex centers the CTA horizontally + lets the inner button
                    expand to 100% via the mobile media query (inline-flex would have
                    constrained the button to its natural width on mobile). */}
                <div className="mom-join-bottom-cta-wrap" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <CtaButton
                        content={content}
                        isLoaded={isLoaded}
                        isSignedIn={isSignedIn}
                        isSubscriber={isSubscriber}
                        demoMode={demoMode}
                        purchaseLoading={purchaseLoading}
                        onSubscribe={onSubscribe}
                        onGoToWorkouts={onGoToWorkouts}
                        justSignedUpRef={justSignedUpRef}
                        plan={plan}
                        variant="bottom"
                    />
                </div>
            </div>
        </section>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  FOOTER — quiet brand line
// ══════════════════════════════════════════════════════════════════════
function Footer() {
    return (
        <footer style={{
            padding: '48px 24px',
            background: 'var(--mom-charcoal)',
            color: 'rgba(255,255,255,0.5)',
            textAlign: 'center',
            position: 'relative',
            zIndex: 2,
        }}>
            <div style={{
                fontFamily: 'var(--mom-font-serif)',
                fontStyle: 'italic',
                fontSize: 18,
                color: '#fff',
                marginBottom: 8,
            }}>
                moments of movement
            </div>
            <div style={{
                fontFamily: 'var(--mom-font-sans)',
                fontSize: 11,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                marginBottom: 24,
            }}>
                with Danielle
            </div>
            <p style={{
                fontFamily: 'var(--mom-font-sans)',
                fontSize: 13,
                marginBottom: 12,
            }}>
                © {new Date().getFullYear()} Moments of Movement. All rights reserved.
            </p>
            <Link to="/terms" style={{
                fontFamily: 'var(--mom-font-sans)',
                fontSize: 12,
                color: 'rgba(255,255,255,0.5)',
                textDecoration: 'underline',
            }}>
                Terms &amp; Privacy
            </Link>
        </footer>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  Responsive CSS — module-level <style> tag injected once
//  CRA + inline-styles convention. Two breakpoint-aware rules:
//   • Hero text+photo two-column on ≥900px
//   • Meet Danielle two-column on ≥700px
// ══════════════════════════════════════════════════════════════════════
const JOIN_STYLES = `
@media (min-width: 900px) {
  .mom-join-hero-grid {
    grid-template-columns: 1.1fr 0.9fr !important;
  }
}
@media (min-width: 700px) {
  .mom-join-meet-grid {
    grid-template-columns: 240px 1fr !important;
    text-align: left !important;
  }
}

/* ── Mobile CTA — full-width thumb-friendly target ──────────────────────
 * The row is column-stacked on all viewports (see ctaRowStyle inline). Mobile
 * (≤600px) overrides alignment from flex-start/center to stretch so the button
 * spans the full width of its container. Price chip + reassurance center for
 * visual balance under the full-width button.
 *
 * Apple HIG: minimum 44×44pt touch target. Our buttons are 50px tall at
 * padding 16px+font 16px+padding 16px — comfortably above. The full-width
 * treatment is about visual confidence at phone widths, not minimum size. */
@media (max-width: 600px) {
  .mom-join-cta-row {
    align-items: stretch !important;
    width: 100%;
  }
  .mom-join-cta-row button {
    width: 100% !important;
    text-align: center !important;
    white-space: normal !important;
  }
  .mom-join-cta-row > span {
    text-align: center !important;
  }
  .mom-join-reassurance {
    text-align: center !important;
  }
  .mom-join-bottom-cta-wrap {
    width: 100% !important;
  }
}

/* ── Lifestyle triptych ─────────────────────────────────────────────────
 * Three portrait tiles. Below 700px viewport the grid auto-fits to 1-2
 * columns based on tile count + minmax. At 700px+ we explicitly lock to
 * 3 columns so tablet widths show the intended triptych layout (without
 * this, three 240px-min tiles + gaps would only fit 2 columns at ~760px
 * and would re-flow awkwardly between 700-800px). */
@media (min-width: 700px) {
  .mom-join-lifestyle-grid {
    grid-template-columns: repeat(3, 1fr) !important;
  }
}

/* ── Smallest phones (≤480px) — extra polish ────────────────────────────
 * Meet Danielle photo scales down. */
@media (max-width: 480px) {
  .mom-join-meet-photo {
    width: 160px !important;
    height: 160px !important;
  }
}
`;

// Inject styles once at module load. Idempotent guard so HMR + multiple mounts
// don't stack duplicate <style> tags.
if (typeof document !== 'undefined' && !document.getElementById('mom-join-styles')) {
    const styleEl = document.createElement('style');
    styleEl.id = 'mom-join-styles';
    styleEl.textContent = JOIN_STYLES;
    document.head.appendChild(styleEl);
}

export default JoinPage;
